import { useEffect, useState } from 'react';
import {
  Col,
  Row,
  Input,
  ModalHeader,
  ModalBody,
  Label,
  ModalFooter,
  Modal,
  Form,
  FormFeedback,
} from 'reactstrap';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { removeFile, uploadFile } from 'services/fileService';
import { RegistrationBoards, User } from 'utility/interface';
import { addNewLand, updateLand } from 'services/lands';
import { indianCities, indianStates } from 'utility/constant';
import { FilePreview } from 'Components/Common/FilePreview';
import { paymentMethods } from 'utility/utilityFunctions';
import { uploadFieldCategoryMap } from 'Components/constants/uploadFieldCategoryMap';

type UploadedFile = {
  url: string;
  uploadedAt: number;
};

type UploadField =
  | 'sanad'
  | 'registrationDocuments'
  | 'propertyCard'
  | 'nonAgriculturalCertificate'
  | 'acquisitionDocument'
  | 'titleDeed'
  | 'propertyMapSurveySketch'
  | 'titleClearance'
  | 'paymentProof'
  | 'saleGiftDeed'
  | 'stampDutyProof'
  | 'mutationRecordProof'
  | 'latestPropertyTaxReceipt'
  | 'surveyNumberRecords'
  | 'mutationEntriesROR'
  | 'oldOwnershipDocuments'
  | 'titleSearchReport'
  | 'governmentAllotmentLetters'
  | 'notice135D'
  | 'villageForm7'
  | 'villageForm8A'
  | 'form6HaqPatraka'
  | 'form6HaqPatrakaNondh'
  | 'utara5253'
  | 'talatiMap'
  | 'collectorOrderNAOrder'
  | 'leaseDeeds'
  | 'encumbranceCertificate'
  | 'landPhotos';

interface CreateLandModelProps {
  isOpen: boolean;
  toggle: () => void;
  organizationId: number;
  organizationName: string;
  trustRegId: number;
  type: string;
  onSuccess: () => void;
  editData?: any;
}

const CreateLandModel = ({
  isOpen,
  toggle,
  organizationId,
  organizationName,
  trustRegId,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type,
  onSuccess,
  editData,
}: CreateLandModelProps) => {
  const [user, setUser] = useState<User>();
  const [registrationType, setRegistrationType] = useState<RegistrationBoards>(
    (Object.values(RegistrationBoards) as string[]).includes(type)
      ? (type as RegistrationBoards)
      : RegistrationBoards.UNREGISTERED,
  );
  const [uploads, setUploads] = useState<Record<UploadField, UploadedFile[]>>({
    sanad: [],
    registrationDocuments: [],
    propertyCard: [],
    nonAgriculturalCertificate: [],
    acquisitionDocument: [],
    titleDeed: [],
    propertyMapSurveySketch: [],
    titleClearance: [],
    paymentProof: [],
    saleGiftDeed: [],
    stampDutyProof: [],
    mutationRecordProof: [],
    latestPropertyTaxReceipt: [],
    surveyNumberRecords: [],
    mutationEntriesROR: [],
    oldOwnershipDocuments: [],
    titleSearchReport: [],
    governmentAllotmentLetters: [],
    notice135D: [],
    villageForm7: [],
    villageForm8A: [],
    form6HaqPatraka: [],
    form6HaqPatrakaNondh: [],
    utara5253: [],
    talatiMap: [],
    collectorOrderNAOrder: [],
    leaseDeeds: [],
    encumbranceCertificate: [],
    landPhotos: [],
  });

  /* ---------- React-Hook-Form & Validation ---------- */
  const validationSchema = Yup.object().shape({
    propertyAddress: Yup.string().required('Enter property address'),
    state: Yup.string().required('Select state'),
    cityDistrict: Yup.string().required('Select city/district'),
    pincode: Yup.string()
      .matches(/^\d{6}$/g, 'Enter valid 6-digit pincode')
      .required('Enter pincode'),

    geoCoordinates: Yup.string()
      .nullable()
      .matches(/^(-?\d{1,2}\.\d+),\s*(-?\d{1,3}\.\d+)$/g, {
        excludeEmptyString: true,
        message: 'Enter coordinates as lat,lng',
      }),

    sheetNo: Yup.number()
      .typeError('Must be number')
      .integer()
      .min(0)
      .nullable(),
    wardNo: Yup.number()
      .typeError('Must be number')
      .integer()
      .min(0)
      .nullable(),
    areaSqMt: Yup.number().typeError('Must be number').positive().nullable(),

    registrationNumber: trustRegId
      ? Yup.string().required('Enter registration number')
      : Yup.string().nullable(),

    registrationDate: trustRegId
      ? Yup.string().required('Select registration date')
      : Yup.string().nullable(),

    propertyCardDetails: Yup.string(),
    typeOfAcquisition: Yup.string(),

    surveyNumber: Yup.string().required('Enter survey number'),
    khasra: Yup.string(),
    plotNumber: Yup.string(),
    anyRemarks: Yup.string(),
    dateOfAcquisition: Yup.string(),
    sellerDonorName: Yup.string(),

    costOfAcquisition: Yup.number()
      .typeError('Must be number')
      .positive()
      .nullable(),

    paymentMethod: Yup.string().nullable(),

    stampDutyPaid: Yup.string(),
    mutationNumber: Yup.string(),
    sanad: Yup.string(),
    registrationDocuments: Yup.string(),
    propertyCard: Yup.string(),
    nonAgriculturalCertificate: Yup.string(),
    acquisitionDocument: Yup.string(),
    titleDeed: Yup.string(),
    propertyMapSurveySketch: Yup.string(),
    titleClearance: Yup.string(),
    paymentProof: Yup.string(),
    saleGiftDeed: Yup.string(),
    stampDutyProof: Yup.string(),
    mutationRecordProof: Yup.string(),
    latestPropertyTaxReceipt: Yup.string(),
    surveyNumberRecords: Yup.string(),
    mutationEntriesROR: Yup.string(),
    oldOwnershipDocuments: Yup.string(),
    titleSearchReport: Yup.string(),
    governmentAllotmentLetters: Yup.string(),
    notice135D: Yup.string(),
    villageForm7: Yup.string(),
    villageForm8A: Yup.string(),
    form6HaqPatraka: Yup.string(),
    form6HaqPatrakaNondh: Yup.string(),
    utara5253: Yup.string(),
    talatiMap: Yup.string(),
    collectorOrderNAOrder: Yup.string(),
    leaseDeeds: Yup.string(),
    encumbranceCertificate: Yup.string(),
    landPhotos: Yup.string(),
  });

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      propertyAddress: '',
      cityDistrict: '',
      state: '',
      pincode: '',
      geoCoordinates: '',
      sheetNo: null,
      wardNo: null,
      areaSqMt: null,
      registrationNumber: '',
      registrationDate: '',
      propertyCardDetails: '',
      typeOfAcquisition: '',
      surveyNumber: '',
      khasra: '',
      plotNumber: '',
      anyRemarks: '',
      dateOfAcquisition: '',
      sellerDonorName: '',
      costOfAcquisition: null,
      paymentMethod: null,
      stampDutyPaid: '',
      mutationNumber: '',
      sanad: '',
      registrationDocuments: '',
      propertyCard: '',
      nonAgriculturalCertificate: '',
      acquisitionDocument: '',
      titleDeed: '',
      propertyMapSurveySketch: '',
      titleClearance: '',
      paymentProof: '',
      saleGiftDeed: '',
      stampDutyProof: '',
      mutationRecordProof: '',
      latestPropertyTaxReceipt: '',
      surveyNumberRecords: '',
      mutationEntriesROR: '',
      oldOwnershipDocuments: '',
      titleSearchReport: '',
      governmentAllotmentLetters: '',
      notice135D: '',
      villageForm7: '',
      villageForm8A: '',
      form6HaqPatraka: '',
      form6HaqPatrakaNondh: '',
      utara5253: '',
      talatiMap: '',
      collectorOrderNAOrder: '',
      leaseDeeds: '',
      encumbranceCertificate: '',
      landPhotos: '',
    },
  });

  const handleMultiUpload = async (
    files: FileList | null,
    field: UploadField,
  ) => {
    if (!files || !files.length) return;
    try {
      const fileArr = Array.from(files);
      const values = getValues();

      const results = await Promise.all(
        fileArr.map(file =>
          uploadFile({
            file,

            category: uploadFieldCategoryMap[field],

            customFileName: `${field}-${Date.now()}`,

            organizationName,

            trustRegNumber: values.registrationNumber,

            surveyNumber: values.surveyNumber,

            propertyName: values.propertyAddress,
          }),
        ),
      );
      const newFiles: UploadedFile[] = results.map(r => ({
        url: r.data.url,
        uploadedAt: Date.now(),
      }));

      setUploads(prev => {
        const updated = [...newFiles, ...prev[field]].sort(
          (a, b) => b.uploadedAt - a.uploadedAt,
        );
        // keep a comma-separated value too (similar to create-property-model)
        setValue(field as any, updated.map(f => f.url).join(','));
        return { ...prev, [field]: updated };
      });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || e?.message || 'Upload failed');
    }
  };

  const removeUploadedFile = async (field: UploadField, index: number) => {
    const file = uploads[field][index];

    setUploads(prev => {
      const updated = prev[field].filter((_, i) => i !== index);
      setValue(field as any, updated.map(f => f.url).join(','));
      return { ...prev, [field]: updated };
    });

    if (file?.url) {
      try {
        await removeFile([file.url]);
      } catch (e: any) {
        toast.error(`Error in removing uploaded file: ${e}`);
        console.error('Delete failed', e);
      }
    }
  };

  useEffect(() => {
    const authUser = sessionStorage.getItem('authUser');
    if (authUser) {
      try {
        setUser(JSON.parse(authUser));
      } catch {
        // ignore parse errors
      }
    }
  }, []);

  useEffect(() => {
    setRegistrationType(
      (Object.values(RegistrationBoards) as string[]).includes(type)
        ? (type as RegistrationBoards)
        : RegistrationBoards.UNREGISTERED,
    );
  }, [type]);

  useEffect(() => {
    if (editData) {
      reset({
        propertyAddress: editData?.propertyAddress || '',
        cityDistrict: editData?.cityDistrict || '',
        state: editData?.state || '',
        pincode: editData?.pincode || '',
        geoCoordinates: editData?.geoCoordinates || '',
        sheetNo: editData?.sheetNo || null,
        wardNo: editData?.wardNo || null,
        areaSqMt: editData?.areaSqMt || null,
        registrationNumber: editData?.registrationNumber || '',
        registrationDate: editData?.registrationDate || '',
        propertyCardDetails: editData?.propertyCardDetails || '',
        typeOfAcquisition: editData?.typeOfAcquisition || '',
        surveyNumber: editData?.surveyNumber || '',
        khasra: editData?.khasra || '',
        plotNumber: editData?.plotNumber || '',
        anyRemarks: editData?.anyRemarks || '',
        dateOfAcquisition: editData?.dateOfAcquisition || '',
        sellerDonorName: editData?.sellerDonorName || '',
        costOfAcquisition: editData?.costOfAcquisition || null,
        paymentMethod: editData?.paymentMethod || null,
        stampDutyPaid: editData?.stampDutyPaid || '',
        mutationNumber: editData?.mutationNumber || '',
      });

      const toUrls = (v: any): string[] => {
        if (!v) return [];
        if (Array.isArray(v)) return v.filter(Boolean);
        if (typeof v === 'string') return v.split(',').filter(Boolean);
        return [];
      };

      const fields: UploadField[] = [
        'sanad',
        'registrationDocuments',
        'propertyCard',
        'nonAgriculturalCertificate',
        'acquisitionDocument',
        'titleDeed',
        'propertyMapSurveySketch',
        'titleClearance',
        'paymentProof',
        'saleGiftDeed',
        'stampDutyProof',
        'mutationRecordProof',
        'latestPropertyTaxReceipt',
        'surveyNumberRecords',
        'mutationEntriesROR',
        'oldOwnershipDocuments',
        'titleSearchReport',
        'governmentAllotmentLetters',
        'notice135D',
        'villageForm7',
        'villageForm8A',
        'form6HaqPatraka',
        'form6HaqPatrakaNondh',
        'utara5253',
        'talatiMap',
        'collectorOrderNAOrder',
        'leaseDeeds',
        'encumbranceCertificate',
        'landPhotos',
      ];

      const next: Record<UploadField, UploadedFile[]> = { ...uploads };
      fields.forEach(f => {
        const urls =
          f === 'registrationDocuments'
            ? toUrls(editData?.registrationDocuments)
            : f === 'mutationRecordProof'
              ? toUrls(editData?.mutationRecordProof)
              : f === 'latestPropertyTaxReceipt'
                ? toUrls(editData?.latestPropertyTaxReceipt)
                : f === 'landPhotos'
                  ? toUrls(editData?.landPhotos)
                  : toUrls(editData?.[f]);

        next[f] = urls.map(url => ({ url, uploadedAt: Date.now() }));
      });
      setUploads(next);
    }
  }, [editData, reset]);

  const resetUploads = () => {
    setUploads({
      sanad: [],
      registrationDocuments: [],
      propertyCard: [],
      nonAgriculturalCertificate: [],
      acquisitionDocument: [],
      titleDeed: [],
      propertyMapSurveySketch: [],
      titleClearance: [],
      paymentProof: [],
      saleGiftDeed: [],
      stampDutyProof: [],
      mutationRecordProof: [],
      latestPropertyTaxReceipt: [],
      surveyNumberRecords: [],
      mutationEntriesROR: [],
      oldOwnershipDocuments: [],
      titleSearchReport: [],
      governmentAllotmentLetters: [],
      notice135D: [],
      villageForm7: [],
      villageForm8A: [],
      form6HaqPatraka: [],
      form6HaqPatrakaNondh: [],
      utara5253: [],
      talatiMap: [],
      collectorOrderNAOrder: [],
      leaseDeeds: [],
      encumbranceCertificate: [],
      landPhotos: [],
    });
  };

  /* ---------- Submit ---------- */
  const onSubmit = async values => {
    try {
      // Merge form values + uploads into payload
      const payload = {
        ...values,
        organizationId,
        registrationId: trustRegId || null,
        sanad: uploads.sanad.map(f => f.url).join(','),
        registrationDocuments: uploads.registrationDocuments
          .map(f => f.url)
          .join(','),
        propertyCard: uploads.propertyCard.map(f => f.url).join(','),
        nonAgriculturalCertificate: uploads.nonAgriculturalCertificate
          .map(f => f.url)
          .join(','),
        acquisitionDocument: uploads.acquisitionDocument
          .map(f => f.url)
          .join(','),
        titleDeed: uploads.titleDeed.map(f => f.url).join(','),
        propertyMapSurveySketch: uploads.propertyMapSurveySketch
          .map(f => f.url)
          .join(','),
        titleClearance: uploads.titleClearance.map(f => f.url).join(','),
        paymentProof: uploads.paymentProof.map(f => f.url).join(','),
        saleGiftDeed: uploads.saleGiftDeed.map(f => f.url).join(','),
        stampDutyProof: uploads.stampDutyProof.map(f => f.url).join(','),
        mutationRecordProof: uploads.mutationRecordProof
          .map(f => f.url)
          .join(','),
        latestPropertyTaxReceipt: uploads.latestPropertyTaxReceipt
          .map(f => f.url)
          .join(','),
        surveyNumberRecords: uploads.surveyNumberRecords
          .map(f => f.url)
          .join(','),
        mutationEntriesROR: uploads.mutationEntriesROR
          .map(f => f.url)
          .join(','),
        oldOwnershipDocuments: uploads.oldOwnershipDocuments
          .map(f => f.url)
          .join(','),
        titleSearchReport: uploads.titleSearchReport.map(f => f.url).join(','),
        governmentAllotmentLetters: uploads.governmentAllotmentLetters
          .map(f => f.url)
          .join(','),
        notice135D: uploads.notice135D.map(f => f.url).join(','),
        villageForm7: uploads.villageForm7.map(f => f.url).join(','),
        villageForm8A: uploads.villageForm8A.map(f => f.url).join(','),
        form6HaqPatraka: uploads.form6HaqPatraka.map(f => f.url).join(','),
        form6HaqPatrakaNondh: uploads.form6HaqPatrakaNondh
          .map(f => f.url)
          .join(','),
        utara5253: uploads.utara5253.map(f => f.url).join(','),
        talatiMap: uploads.talatiMap.map(f => f.url).join(','),
        collectorOrderNAOrder: uploads.collectorOrderNAOrder
          .map(f => f.url)
          .join(','),
        leaseDeeds: uploads.leaseDeeds.map(f => f.url).join(','),
        encumbranceCertificate: uploads.encumbranceCertificate
          .map(f => f.url)
          .join(','),
        landPhotos: uploads.landPhotos.map(f => f.url).join(','),
      };
      if (editData) {
        await updateLand(editData.id, payload);
      } else {
        await addNewLand(payload);
      }
      toast.success(editData ? 'Land updated' : 'Land added');
      onSuccess();
      toggle();
      reset();
      resetUploads();
    } catch (e: any) {
      toast.error(`Error saving land: ${e}`);
    }
  };

  /* ---------- UI ---------- */
  return (
    <Modal isOpen={isOpen} toggle={toggle} size="xl" centered backdrop="static">
      <ModalHeader toggle={toggle} className="bg-light p-3">
        {editData ? 'Edit Land' : 'Add Land'}
      </ModalHeader>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalBody style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Row className="g-3">
            {/* Basic Info */}
            <h6>Basic Details</h6>
            <Col lg={12}>
              <Label className="form-label">Property Address *</Label>
              <Controller
                name="propertyAddress"
                control={control}
                render={({ field }) => (
                  <Input {...field} invalid={!!errors.propertyAddress} />
                )}
              />
              {errors.propertyAddress && (
                <FormFeedback>{errors.propertyAddress.message}</FormFeedback>
              )}
            </Col>
            <Col lg={6}>
              <Label className="form-label">State *</Label>
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <Input type="select" {...field} invalid={!!errors.state}>
                    <option value="">Select</option>
                    {indianStates.map(c => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Input>
                )}
              />
              {errors.state && (
                <FormFeedback>{errors.state.message}</FormFeedback>
              )}
            </Col>
            <Col lg={6}>
              <Label className="form-label">City / District *</Label>
              <Controller
                name="cityDistrict"
                control={control}
                render={({ field }) => (
                  <Input
                    type="select"
                    {...field}
                    invalid={!!errors.cityDistrict}
                  >
                    <option value="">Select</option>
                    {indianCities.map(c => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Input>
                )}
              />
              {errors.cityDistrict && (
                <FormFeedback>{errors.cityDistrict.message}</FormFeedback>
              )}
            </Col>
            <Col lg={4}>
              <Label className="form-label">Pincode *</Label>
              <Controller
                name="pincode"
                control={control}
                render={({ field }) => (
                  <Input {...field} invalid={!!errors.pincode} />
                )}
              />
              {errors.pincode && (
                <FormFeedback>{errors.pincode.message}</FormFeedback>
              )}
            </Col>
            <Col lg={4}>
              <Label className="form-label">Ward No.</Label>
              <Controller
                name="wardNo"
                control={control}
                render={({ field }) => <Input type="number" {...field} />}
              />
              {errors.wardNo && (
                <FormFeedback>{errors.wardNo.message}</FormFeedback>
              )}
            </Col>
            <Col lg={4}>
              <Label className="form-label">Sheet No.</Label>
              <Controller
                name="sheetNo"
                control={control}
                render={({ field }) => <Input type="number" {...field} />}
              />
              {errors.sheetNo && (
                <FormFeedback>{errors.sheetNo.message}</FormFeedback>
              )}
            </Col>
            <Col lg={6}>
              <Label className="form-label">Area (Sq. Mt.)</Label>
              <Controller
                name="areaSqMt"
                control={control}
                render={({ field }) => (
                  <Input type="number" {...field} invalid={!!errors.areaSqMt} />
                )}
              />
              {errors.areaSqMt && (
                <FormFeedback>{errors.areaSqMt.message}</FormFeedback>
              )}
            </Col>
            <Col lg={6}>
              <Label className="form-label">Geo-coordinates (lat,lng)</Label>
              <Controller
                name="geoCoordinates"
                control={control}
                render={({ field }) => (
                  <Input placeholder="19.0760,72.8777" {...field} />
                )}
              />
              {errors.geoCoordinates && (
                <FormFeedback>{errors.geoCoordinates.message}</FormFeedback>
              )}
            </Col>

            {/* Registration Details */}
            <hr className="my-2" />
            <h6>Registration Details</h6>
            <Col lg={6}>
              <Label className="form-label">
                Registration Number {trustRegId ? '*' : null}
              </Label>
              <Controller
                name="registrationNumber"
                control={control}
                render={({ field }) => (
                  <Input {...field} invalid={!!errors.registrationNumber} />
                )}
              />
              {errors.registrationNumber && (
                <FormFeedback>{errors.registrationNumber.message}</FormFeedback>
              )}
            </Col>
            <Col lg={6}>
              <Label className="form-label">
                Registration Date {trustRegId ? '*' : null}
              </Label>
              <Controller
                name="registrationDate"
                control={control}
                render={({ field }) => (
                  <Input
                    type="date"
                    {...field}
                    invalid={!!errors.registrationDate}
                  />
                )}
              />
              {errors.registrationDate && (
                <FormFeedback>{errors.registrationDate.message}</FormFeedback>
              )}
            </Col>

            {/* Survey Details */}
            <hr className="my-2" />
            <h6>Survey Details</h6>
            <Col lg={4}>
              <Label className="form-label">Survey Number *</Label>
              <Controller
                name="surveyNumber"
                control={control}
                render={({ field }) => (
                  <Input {...field} invalid={!!errors.surveyNumber} />
                )}
              />
              {errors.surveyNumber && (
                <FormFeedback>{errors.surveyNumber.message}</FormFeedback>
              )}
            </Col>
            <Col lg={4}>
              <Label className="form-label">Khasra</Label>
              <Controller
                name="khasra"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.khasra && (
                <FormFeedback>{errors.khasra.message}</FormFeedback>
              )}
            </Col>
            <Col lg={4}>
              <Label className="form-label">Plot Number</Label>
              <Controller
                name="plotNumber"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.plotNumber && (
                <FormFeedback>{errors.plotNumber.message}</FormFeedback>
              )}
            </Col>

            {/* Acquisition Details */}
            <hr className="my-2" />
            <h6>Acquisition Details</h6>
            <Col lg={6}>
              <Label className="form-label">Type of Acquisition</Label>
              <Controller
                name="typeOfAcquisition"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.typeOfAcquisition && (
                <FormFeedback>{errors.typeOfAcquisition.message}</FormFeedback>
              )}
            </Col>
            <Col lg={6}>
              <Label className="form-label">Date of Acquisition</Label>
              <Controller
                name="dateOfAcquisition"
                control={control}
                render={({ field }) => <Input type="date" {...field} />}
              />
              {errors.dateOfAcquisition && (
                <FormFeedback>{errors.dateOfAcquisition.message}</FormFeedback>
              )}
            </Col>
            <Col lg={4}>
              <Label className="form-label">Seller / Donor Name</Label>
              <Controller
                name="sellerDonorName"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.sellerDonorName && (
                <FormFeedback>{errors.sellerDonorName.message}</FormFeedback>
              )}
            </Col>
            <Col lg={4}>
              <Label className="form-label">Cost of Acquisition</Label>
              <Controller
                name="costOfAcquisition"
                control={control}
                render={({ field }) => (
                  <Input
                    type="number"
                    {...field}
                    invalid={!!errors.costOfAcquisition}
                  />
                )}
              />
              {errors.costOfAcquisition && (
                <FormFeedback>{errors.costOfAcquisition.message}</FormFeedback>
              )}
            </Col>
            <Col lg={4}>
              <Label className="form-label">Payment Method</Label>
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <Input
                    type="select"
                    {...field}
                    invalid={!!errors.paymentMethod}
                  >
                    <option value="">Select</option>
                    {paymentMethods.map(c => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Input>
                )}
              />
              {errors.paymentMethod && (
                <FormFeedback>{errors.paymentMethod.message}</FormFeedback>
              )}
            </Col>

            {/* Remarks */}
            <Col lg={12}>
              <Label className="form-label">Any other remarks</Label>
              <Controller
                name="anyRemarks"
                control={control}
                render={({ field }) => (
                  <Input type="textarea" rows="2" {...field} />
                )}
              />
              {errors.anyRemarks && (
                <FormFeedback>{errors.anyRemarks.message}</FormFeedback>
              )}
            </Col>

            {/* ---------- FILE UPLOADS (Single) ---------- */}
            <hr className="my-2" />
            <h6>Documents Upload</h6>
            {[
              {
                label: 'Sanad',
                field: 'sanad' as UploadField,
              },
              {
                label: 'Registration Documents (7/12, 6, 8/A)',
                field: 'registrationDocuments' as UploadField,
              },
              {
                label: 'Property Card',
                field: 'propertyCard' as UploadField,
              },
              {
                label: 'Non-Agricultural Certificate',
                field: 'nonAgriculturalCertificate' as UploadField,
              },
              {
                label: 'Acquisition Document',
                field: 'acquisitionDocument' as UploadField,
              },
              {
                label: 'Title Deed',
                field: 'titleDeed' as UploadField,
              },
              {
                label: 'Property Map / Survey Sketch',
                field: 'propertyMapSurveySketch' as UploadField,
              },
              {
                label: 'Title Clearance',
                field: 'titleClearance' as UploadField,
              },
              {
                label: 'Payment Proof',
                field: 'paymentProof' as UploadField,
              },
              {
                label: 'Sale / Gift Deed',
                field: 'saleGiftDeed' as UploadField,
              },
              {
                label: 'Stamp Duty Proof',
                field: 'stampDutyProof' as UploadField,
              },
              {
                label: 'Mutation Record Proof',
                field: 'mutationRecordProof' as UploadField,
              },
              {
                label: 'Latest Property Tax Receipt',
                field: 'latestPropertyTaxReceipt' as UploadField,
              },

              // Extra Land document fields
              {
                label: 'Survey Number Records',
                field: 'surveyNumberRecords' as UploadField,
              },
              {
                label: 'Mutation Entries (ROR)',
                field: 'mutationEntriesROR' as UploadField,
              },
              {
                label: 'Old Ownership Documents',
                field: 'oldOwnershipDocuments' as UploadField,
              },
              {
                label: 'Title Search Report',
                field: 'titleSearchReport' as UploadField,
              },
              {
                label: 'Government Allotment Letters',
                field: 'governmentAllotmentLetters' as UploadField,
              },
              {
                label: '135-D Notice',
                field: 'notice135D' as UploadField,
              },
              {
                label: 'Village Form 7 (7/12 Extract)',
                field: 'villageForm7' as UploadField,
              },
              {
                label: 'Village Form 8A',
                field: 'villageForm8A' as UploadField,
              },
              {
                label: 'Form No. 6 (Haq Patraka)',
                field: 'form6HaqPatraka' as UploadField,
              },
              {
                label: 'Form No. 6 (Haq Patraka Nondh)',
                field: 'form6HaqPatrakaNondh' as UploadField,
              },
              {
                label: '52/53 Utara',
                field: 'utara5253' as UploadField,
              },
              {
                label: 'Talati Map (Half/Full)',
                field: 'talatiMap' as UploadField,
              },
              {
                label: 'Collector Order / N.A. Order',
                field: 'collectorOrderNAOrder' as UploadField,
              },
              {
                label: 'Lease Deeds',
                field: 'leaseDeeds' as UploadField,
              },
              {
                label: 'Encumbrance Certificate',
                field: 'encumbranceCertificate' as UploadField,
              },
            ].map(upload => (
              <Col lg={6} key={upload.field}>
                <Label className="form-label">{upload.label}</Label>
                <Input
                  type="file"
                  multiple
                  onChange={e =>
                    handleMultiUpload(e.target.files, upload.field)
                  }
                />
                <FilePreview
                  files={uploads[upload.field]}
                  onRemove={i => removeUploadedFile(upload.field, i)}
                />
              </Col>
            ))}

            {/* Property Card Details */}
            <Col lg={12}>
              <Label className="form-label">Property Card Details</Label>
              <Controller
                name="propertyCardDetails"
                control={control}
                render={({ field }) => (
                  <Input type="textarea" rows="2" {...field} />
                )}
              />
              {errors.propertyCardDetails && (
                <FormFeedback>
                  {errors.propertyCardDetails.message}
                </FormFeedback>
              )}
            </Col>

            {/* Stamp duty paid amount */}
            <Col lg={6}>
              <Label className="form-label">Stamp Duty Paid Amount</Label>
              <Controller
                name="stampDutyPaid"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.stampDutyPaid && (
                <FormFeedback>{errors.stampDutyPaid.message}</FormFeedback>
              )}
            </Col>
            {/* Mutation record number */}
            <Col lg={6}>
              <Label className="form-label">Mutation Record Number</Label>
              <Controller
                name="mutationNumber"
                control={control}
                render={({ field }) => <Input {...field} />}
              />
              {errors.mutationNumber && (
                <FormFeedback>{errors.mutationNumber.message}</FormFeedback>
              )}
            </Col>

            {/* Land Photos (multiple) */}
            <Col lg={12}>
              <Label className="form-label">Land Photos (multiple)</Label>
              <Input
                type="file"
                multiple
                accept="image/*"
                onChange={e => handleMultiUpload(e.target.files, 'landPhotos')}
              />
              <FilePreview
                files={uploads.landPhotos}
                onRemove={i => removeUploadedFile('landPhotos', i)}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn btn-light" onClick={toggle}>
            Close
          </button>
          <button type="submit" className="btn btn-success">
            {editData ? 'Update' : 'Create'}
          </button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default CreateLandModel;
